/**
 * Analytical Model Manager
 * 
 * Manages the analytical representation of structural elements.
 * Provides node graph, member definitions, and boundary conditions.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';

// ==================== Types ====================

export type BoundaryCondition = 'Free' | 'Pinned' | 'Fixed' | 'Roller' | 'Spring';
export type ReleaseType = 'None' | 'Moment' | 'Shear' | 'Axial' | 'All';

export interface AnalyticalNode {
    id: string;
    position: [number, number, number];
    boundaryCondition: BoundaryCondition;
    springStiffness?: {
        translationX?: number;
        translationY?: number;
        translationZ?: number;
        rotationX?: number;
        rotationY?: number;
        rotationZ?: number;
    };
    supportReactions?: {
        fx: number;
        fy: number;
        fz: number;
        mx: number;
        my: number;
        mz: number;
    };
}

export interface AnalyticalMember {
    id: string;
    elementId: string; // Reference to BIMElement
    startNodeId: string;
    endNodeId: string;

    // Section Properties
    section: {
        area: number;              // m²
        momentOfInertiaX: number;  // m⁴ (Ix - strong axis)
        momentOfInertiaY: number;  // m⁴ (Iy - weak axis)
        momentOfInertiaZ: number;  // m⁴ (Iz - torsional)
        sectionModulusX: number;   // m³
        sectionModulusY: number;   // m³
    };

    // Material Properties
    material: {
        youngsModulus: number;     // Pa (E)
        shearModulus: number;      // Pa (G)
        poissonRatio: number;      // ν
        density: number;           // kg/m³
    };

    // End Releases
    startRelease: {
        momentX: boolean;
        momentY: boolean;
        momentZ: boolean;
        axial: boolean;
    };
    endRelease: {
        momentX: boolean;
        momentY: boolean;
        momentZ: boolean;
        axial: boolean;
    };

    // Results (populated after analysis)
    results?: {
        axialForce: number[];      // At stations along member
        shearY: number[];
        shearZ: number[];
        momentY: number[];
        momentZ: number[];
        torsion: number[];
        deflection: number[];
    };
}

export interface AnalyticalSurface {
    id: string;
    elementId: string;
    boundaryNodeIds: string[];
    thickness: number;
    material: {
        youngsModulus: number;
        poissonRatio: number;
    };
}

export interface AnalyticalModel {
    nodes: Map<string, AnalyticalNode>;
    members: Map<string, AnalyticalMember>;
    surfaces: Map<string, AnalyticalSurface>;

    // Analysis metadata
    isValid: boolean;
    lastAnalysisTime?: number;
    errorMessages: string[];
}

// ==================== Standard Sections ====================

export const STANDARD_SECTIONS: Record<string, Partial<AnalyticalMember['section']>> = {
    'W12x26': { area: 0.00484, momentOfInertiaX: 0.0000884, momentOfInertiaY: 0.0000172 },
    'W14x30': { area: 0.00568, momentOfInertiaX: 0.000121, momentOfInertiaY: 0.0000190 },
    'W16x40': { area: 0.00761, momentOfInertiaX: 0.000216, momentOfInertiaY: 0.0000287 },
    'W18x50': { area: 0.00952, momentOfInertiaX: 0.000356, momentOfInertiaY: 0.0000408 },
    'W21x62': { area: 0.01181, momentOfInertiaX: 0.000540, momentOfInertiaY: 0.0000570 },
    'HSS6x6x1/4': { area: 0.00342, momentOfInertiaX: 0.0000287, momentOfInertiaY: 0.0000287 },
    'HSS8x8x1/2': { area: 0.00890, momentOfInertiaX: 0.0000890, momentOfInertiaY: 0.0000890 },
};

export const STANDARD_MATERIALS: Record<string, AnalyticalMember['material']> = {
    'Steel-A992': { youngsModulus: 200e9, shearModulus: 77e9, poissonRatio: 0.3, density: 7850 },
    'Steel-A36': { youngsModulus: 200e9, shearModulus: 77e9, poissonRatio: 0.3, density: 7850 },
    'Concrete-C25': { youngsModulus: 31e9, shearModulus: 13e9, poissonRatio: 0.2, density: 2400 },
    'Concrete-C30': { youngsModulus: 33e9, shearModulus: 14e9, poissonRatio: 0.2, density: 2400 },
    'Timber-GL24': { youngsModulus: 11.6e9, shearModulus: 0.72e9, poissonRatio: 0.4, density: 420 },
};

// ==================== Analytical Model Manager ====================

export class AnalyticalModelManager {
    private model: AnalyticalModel;
    private elementToMemberMap: Map<string, string> = new Map();

    constructor() {
        this.model = {
            nodes: new Map(),
            members: new Map(),
            surfaces: new Map(),
            isValid: false,
            errorMessages: []
        };
    }

    // ==================== Auto-Generation ====================

    /**
     * Auto-generate analytical model from physical model.
     * Iterates through all structural elements in the project.
     */
    public generateFromProject(elements: any[]): void {
        // Clear existing
        this.model.nodes.clear();
        this.model.members.clear();
        this.model.surfaces.clear();
        this.elementToMemberMap.clear();

        elements.forEach(el => {
            if (el.category === 'Structural Columns') {
                // Vertical Member
                const geom = el.geometryData;
                const start: [number, number, number] = [geom.location[0], geom.location[1], geom.bottomOffset];
                const end: [number, number, number] = [geom.location[0], geom.location[1], geom.location[2] + geom.height + geom.topOffset];

                // Usually Columns are "Fixed" at bottom if at base level
                const member = this.createMemberFromElement(el.id, start, end);

                // Heuristic: If bottom is at z=0, assume fixed.
                if (start[2] <= 0.001) {
                    this.setBoundaryCondition(member.startNodeId, 'Fixed');
                }
            }
            else if (el.category === 'Structural Framing') {
                // Beams/Braces
                const geom = el.geometryData;
                this.createMemberFromElement(el.id, geom.start, geom.end);
            }
            else if (el.category === 'Floors' && el.geometryData.isStructural) {
                // Slabs -> Analytical Surfaces
                // Use geometry boundary
                // Simplify: Just take boundary points
                const boundary3D = el.geometryData.boundary.map((p: any) =>
                    [p[0], p[1], el.geometryData.heightOffsetFromLevel] as [number, number, number]
                );
                this.createSurfaceFromElement(el.id, boundary3D, el.geometryData.thickness);
            }
        });

        // Post-process: Merge coincident nodes?
        // createMemberFromElement already uses getOrCreateNode(position), so standard nodes are unified.
        // However, we might need tolerance checks if physical models slighty misalign.
        // For now, rely on getOrCreateNode tolerance (0.01).

        this.validateModel();
    }

    // ==================== Node Operations ====================

    public createNode(
        position: [number, number, number],
        boundaryCondition: BoundaryCondition = 'Free'
    ): AnalyticalNode {
        const node: AnalyticalNode = {
            id: uuidv4(),
            position,
            boundaryCondition
        };
        this.model.nodes.set(node.id, node);
        this.invalidateModel();
        return node;
    }

    public findNodeAtPosition(position: [number, number, number], tolerance: number = 0.01): AnalyticalNode | null {
        const pos = new THREE.Vector3(...position);
        for (const node of this.model.nodes.values()) {
            const nodePos = new THREE.Vector3(...node.position);
            if (pos.distanceTo(nodePos) < tolerance) {
                return node;
            }
        }
        return null;
    }

    public getOrCreateNode(position: [number, number, number], boundaryCondition?: BoundaryCondition): AnalyticalNode {
        const existing = this.findNodeAtPosition(position);
        if (existing) {
            // Upgrade boundary condition if specified
            if (boundaryCondition && boundaryCondition !== 'Free') {
                existing.boundaryCondition = boundaryCondition;
            }
            return existing;
        }
        return this.createNode(position, boundaryCondition);
    }

    public setBoundaryCondition(nodeId: string, condition: BoundaryCondition): void {
        const node = this.model.nodes.get(nodeId);
        if (node) {
            node.boundaryCondition = condition;
            this.invalidateModel();
        }
    }

    // ==================== Member Operations ====================

    public createMemberFromElement(
        elementId: string,
        startPos: [number, number, number],
        endPos: [number, number, number],
        sectionName: string = 'W12x26',
        materialName: string = 'Steel-A992'
    ): AnalyticalMember {
        const startNode = this.getOrCreateNode(startPos);
        const endNode = this.getOrCreateNode(endPos);

        const section = STANDARD_SECTIONS[sectionName] || STANDARD_SECTIONS['W12x26'];
        const material = STANDARD_MATERIALS[materialName] || STANDARD_MATERIALS['Steel-A992'];

        const member: AnalyticalMember = {
            id: uuidv4(),
            elementId,
            startNodeId: startNode.id,
            endNodeId: endNode.id,
            section: {
                area: section.area || 0.01,
                momentOfInertiaX: section.momentOfInertiaX || 0.0001,
                momentOfInertiaY: section.momentOfInertiaY || 0.00005,
                momentOfInertiaZ: 0.00001, // Torsional
                sectionModulusX: Math.sqrt(section.momentOfInertiaX || 0.0001) * 10,
                sectionModulusY: Math.sqrt(section.momentOfInertiaY || 0.00005) * 10
            },
            material,
            startRelease: { momentX: false, momentY: false, momentZ: false, axial: false },
            endRelease: { momentX: false, momentY: false, momentZ: false, axial: false }
        };

        this.model.members.set(member.id, member);
        this.elementToMemberMap.set(elementId, member.id);
        this.invalidateModel();

        return member;
    }

    public setMemberRelease(memberId: string, end: 'start' | 'end', release: Partial<AnalyticalMember['startRelease']>): void {
        const member = this.model.members.get(memberId);
        if (!member) return;

        if (end === 'start') {
            member.startRelease = { ...member.startRelease, ...release };
        } else {
            member.endRelease = { ...member.endRelease, ...release };
        }
        this.invalidateModel();
    }

    public getMemberByElementId(elementId: string): AnalyticalMember | undefined {
        const memberId = this.elementToMemberMap.get(elementId);
        return memberId ? this.model.members.get(memberId) : undefined;
    }

    // ==================== Surface Operations ====================

    public createSurfaceFromElement(
        elementId: string,
        boundaryPositions: [number, number, number][],
        thickness: number,
        materialName: string = 'Concrete-C30'
    ): AnalyticalSurface {
        const nodeIds: string[] = [];
        for (const pos of boundaryPositions) {
            const node = this.getOrCreateNode(pos);
            nodeIds.push(node.id);
        }

        const material = STANDARD_MATERIALS[materialName] || STANDARD_MATERIALS['Concrete-C30'];

        const surface: AnalyticalSurface = {
            id: uuidv4(),
            elementId,
            boundaryNodeIds: nodeIds,
            thickness,
            material: {
                youngsModulus: material.youngsModulus,
                poissonRatio: material.poissonRatio
            }
        };

        this.model.surfaces.set(surface.id, surface);
        this.invalidateModel();

        return surface;
    }

    // ==================== Model Validation ====================

    public validateModel(): boolean {
        this.model.errorMessages = [];

        // 1. Check for disconnected nodes
        const connectedNodeIds = new Set<string>();
        for (const member of this.model.members.values()) {
            connectedNodeIds.add(member.startNodeId);
            connectedNodeIds.add(member.endNodeId);
        }
        for (const surface of this.model.surfaces.values()) {
            surface.boundaryNodeIds.forEach(id => connectedNodeIds.add(id));
        }

        for (const node of this.model.nodes.values()) {
            if (!connectedNodeIds.has(node.id)) {
                this.model.errorMessages.push(`Disconnected node: ${node.id}`);
            }
        }

        // 2. Check for minimum supports
        let fixedDOFs = 0;
        for (const node of this.model.nodes.values()) {
            switch (node.boundaryCondition) {
                case 'Fixed': fixedDOFs += 6; break;
                case 'Pinned': fixedDOFs += 3; break;
                case 'Roller': fixedDOFs += 1; break;
            }
        }

        if (fixedDOFs < 3) {
            this.model.errorMessages.push('Insufficient supports: Model is unstable');
        }

        // 3. Check member lengths
        for (const member of this.model.members.values()) {
            const startNode = this.model.nodes.get(member.startNodeId);
            const endNode = this.model.nodes.get(member.endNodeId);
            if (startNode && endNode) {
                const length = new THREE.Vector3(...startNode.position)
                    .distanceTo(new THREE.Vector3(...endNode.position));
                if (length < 0.001) {
                    this.model.errorMessages.push(`Zero-length member: ${member.id}`);
                }
            }
        }

        this.model.isValid = this.model.errorMessages.length === 0;
        return this.model.isValid;
    }

    // ==================== Getters ====================

    public getModel(): AnalyticalModel {
        return this.model;
    }

    public getNodes(): AnalyticalNode[] {
        return Array.from(this.model.nodes.values());
    }

    public getMembers(): AnalyticalMember[] {
        return Array.from(this.model.members.values());
    }

    public getSurfaces(): AnalyticalSurface[] {
        return Array.from(this.model.surfaces.values());
    }

    public isModelValid(): boolean {
        return this.model.isValid;
    }

    public getErrorMessages(): string[] {
        return this.model.errorMessages;
    }

    private invalidateModel(): void {
        this.model.isValid = false;
        this.model.lastAnalysisTime = undefined;
    }

    // ==================== Export ====================

    public exportToJSON(): string {
        return JSON.stringify({
            nodes: Array.from(this.model.nodes.entries()),
            members: Array.from(this.model.members.entries()),
            surfaces: Array.from(this.model.surfaces.entries())
        }, null, 2);
    }
}
